// Schemat för prismotorns resultat, QuoteCalculation i packages/pricing.
// Prismotorn har egna typer och använder inte zod. Schemat beskriver svaret i
// API:t och OpenAPI-specen, och ett typtest i packages/pricing visar att det
// stämmer exakt med typerna där.
import { z } from 'zod';
import { billingSchema, hourlyDisplaySchema, legalFormSchema, oreSchema } from './catalog.js';

export const amountSchema = z.object({ low: oreSchema, high: oreSchema });
export const rangeSchema = z.object({ low: z.number(), high: z.number() });

export const quoteLineSchema = z.object({
  serviceId: z.string(),
  label: z.string(),
  billing: billingSchema,
  includes: z.array(z.string()),
  reason: z.string(),
  amount: amountSchema,
  estimated: z.boolean(),
  units: z.object({ quantity: z.number(), unit: z.string(), unitPrice: oreSchema }).exactOptional(),
  time: z
    .object({ hours: rangeSchema, rate: oreSchema, display: hourlyDisplaySchema })
    .exactOptional(),
});

export const quotePeriodSchema = z.object({
  billing: billingSchema,
  lines: z.array(quoteLineSchema),
  adjustment: z.object({ percent: z.number(), amount: amountSchema }).exactOptional(),
  total: amountSchema,
  vat: amountSchema,
});

export const reviewReasonSchema = z.discriminatedUnion('code', [
  z.object({
    code: z.literal('driverAboveHighestTier'),
    serviceId: z.string(),
    driver: z.string(),
    value: z.number(),
    highestTier: z.number(),
  }),
  z.object({
    code: z.literal('noPriceForLegalForm'),
    serviceId: z.string(),
    legalForm: legalFormSchema,
  }),
  z.object({
    code: z.literal('monthlyTotalAboveLimit'),
    monthlyTotal: oreSchema,
    limit: oreSchema,
  }),
  z.object({ code: z.literal('noLines') }),
]);

export const excludedServiceSchema = z.object({
  serviceId: z.string(),
  reason: z.enum(['notForLegalForm', 'noPriceForLegalForm']),
});

export const quoteCalculationSchema = z.object({
  periods: z.array(quotePeriodSchema),
  optionalLines: z.array(quoteLineSchema),
  reviewReasons: z.array(reviewReasonSchema),
  excludedServices: z.array(excludedServiceSchema),
  adjustment: z
    .object({ requestedPercent: z.number(), appliedPercent: z.number(), reason: z.string() })
    .exactOptional(),
});

/** Samma typ som QuoteCalculation i packages/pricing, härledd från schemat. */
export type QuoteCalculationOutput = z.infer<typeof quoteCalculationSchema>;
